import dayjs from 'dayjs';
import {
  CampaignFilterEnums,
  CampaignFilterMetricEnums,
  CampaignFilterStatusEnums,
} from '../../../../enums/CampaignFilterEnums';
import { DEMAND_OVERVIEW_METRIC_GROUPS_BY_TAB } from '../../enums/demandOverview';
import { buildDemandOverviewMetricFilters } from './metricFilters';

function getDate(date) {
  const parsedDate = dayjs(date);

  return parsedDate.isValid() ? parsedDate.startOf('day') : null;
}

function isSameDate(date, compareDate) {
  return Boolean(date?.isSame(compareDate, 'day'));
}

function isWithinNextDays(date, today, days) {
  if (!date) return false;

  const endDate = today.add(days, 'day');

  return date.valueOf() >= today.valueOf() && date.valueOf() <= endDate.valueOf();
}

function createDemandOverviewBuckets(groupConfigs) {
  return groupConfigs.reduce((groups, groupConfig) => {
    groups[groupConfig.id] = groupConfig.metrics.reduce((metrics, metricConfig) => {
      metrics[metricConfig.id] = [];
      return metrics;
    }, {});

    return groups;
  }, {});
}

function collectId(bucket, groupId, metricId, id) {
  if (id == null || !bucket[groupId]?.[metricId]) return;

  bucket[groupId][metricId].push(id);
}

function collectDemandOverviewIds(groupConfigs, payload) {
  const buckets = createDemandOverviewBuckets(groupConfigs);
  if (!Array.isArray(payload) || payload.length === 0) return buckets;

  const today = dayjs().startOf('day');
  const hasReachedGroup = Boolean(buckets[CampaignFilterEnums.REACHED]);
  const hasScheduledGroup = Boolean(buckets[CampaignFilterEnums.SCHEDULED]);
  const hasEndingGroup = Boolean(buckets[CampaignFilterEnums.ENDING]);

  payload.forEach(item => {
    const meta = item?.meta || {};
    const id = meta.id;
    const state = meta.state || {};
    const activeMetricId = buckets[CampaignFilterEnums.ACTIVE]?.campaigns
      ? 'campaigns'
      : 'lineItems';

    if (state.active === true) {
      collectId(buckets, CampaignFilterEnums.ACTIVE, activeMetricId, id);
    }

    if (!hasReachedGroup && !hasScheduledGroup && !hasEndingGroup) return;

    const details = state.details || {};
    const todayLimit = hasReachedGroup ? Number(details.today_limit) : 0;

    if (state.active !== true && todayLimit === CampaignFilterStatusEnums.TODAY_LIMIT_REACHED) {
      collectId(buckets, CampaignFilterEnums.REACHED, CampaignFilterMetricEnums.TODAY_LIMIT, id);
    } else if (state?.active !== true && hasScheduledGroup) {
      const schedule = Number(details?.schedule);

      if (schedule === CampaignFilterStatusEnums.SCHEDULED_PAUSED) {
        collectId(buckets, CampaignFilterEnums.SCHEDULED, CampaignFilterMetricEnums.PAUSED, id);
      } else if (schedule === CampaignFilterStatusEnums.SCHEDULED) {
        const startDate = getDate(meta?.start);

        if (isSameDate(startDate, today)) {
          collectId(buckets, CampaignFilterEnums.SCHEDULED, CampaignFilterMetricEnums.TODAY, id);
        }

        if (isWithinNextDays(startDate, today, 7)) {
          collectId(
            buckets,
            CampaignFilterEnums.SCHEDULED,
            CampaignFilterMetricEnums.NEXT_7_DAYS,
            id
          );
        }
      }
    }

    if (!hasEndingGroup) return;

    const endDate = getDate(meta?.end);

    if (isSameDate(endDate, today)) {
      collectId(buckets, CampaignFilterEnums.ENDING, CampaignFilterMetricEnums.TODAY, id);
    }

    if (isWithinNextDays(endDate, today, 3)) {
      collectId(buckets, CampaignFilterEnums.ENDING, CampaignFilterMetricEnums.NEXT_3_DAYS, id);
    }

    if (isWithinNextDays(endDate, today, 7)) {
      collectId(buckets, CampaignFilterEnums.ENDING, CampaignFilterMetricEnums.NEXT_7_DAYS, id);
    }
  });

  return buckets;
}

function transformDemandOverview(tabId, payload) {
  const groupConfigs = DEMAND_OVERVIEW_METRIC_GROUPS_BY_TAB[tabId] || [];
  if (payload === undefined) return groupConfigs;
  if (!Array.isArray(payload) || payload.length === 0) return [];

  const buckets = collectDemandOverviewIds(groupConfigs, payload);
  return groupConfigs.map(groupConfig => {
    return {
      ...groupConfig,
      metrics: groupConfig.metrics.map(metricConfig => {
        const ids = buckets[groupConfig.id]?.[metricConfig.id] || [];

        return {
          ...metricConfig,
          filters: buildDemandOverviewMetricFilters(groupConfig.id, metricConfig.id),
          ids,
          value: ids.length,
        };
      }),
    };
  });
}

export { transformDemandOverview };
